// ============================================
// UI Rendering for Progressive Disclosure
// Maya presents, we render what she describes
// ============================================

import type {
  Flight,
  Resort,
  ShowFlightsData,
  ShowResortsData,
  MayaFlightOption,
  MayaResort,
  CabinExperience
} from './data';
import {
  getResortImage,
  getResortHighlights,
  getAirportCode,
  generateFlightNumber,
  estimateFlightDuration,
  parsePrice,
  normalizeCabinClass,
  getCabinExperience,
  MOCK_FLIGHTS,
  MOCK_RESORTS
} from './data';

const flightsContent = document.getElementById('flights-content')!;
const resortsContent = document.getElementById('resorts-content')!;

// Callback for when user interacts
let onResortSelect: ((name: string) => void) | null = null;
let onFlightSelect: ((flight: any) => void) | null = null;
let onCabinSelect: ((cabinClass: string) => void) | null = null;

// State for detail views (so we can go back)
let currentResortsData: ShowResortsData | null = null;
let currentFlightsData: ShowFlightsData | null = null;

export function setOnResortSelect(callback: (name: string) => void) {
  onResortSelect = callback;
}

export function setOnFlightSelect(callback: (flight: any) => void) {
  onFlightSelect = callback;
}

export function setOnCabinSelect(callback: (cabinClass: string) => void) {
  onCabinSelect = callback;
}

// ============================================
// Dynamic Resort Rendering - The Recommendation
// Maya's pick is the hero, alternatives are secondary
// ============================================

export function renderDynamicResorts(data: ShowResortsData) {
  const { destination, resorts } = data;

  if (!resorts || resorts.length === 0) {
    renderResorts(MOCK_RESORTS, {});
    return;
  }

  // Store for back navigation
  currentResortsData = data;

  // Find Maya's pick (highlighted) or default to first
  const allResorts = resorts.slice(0, 3);
  const heroResort = allResorts.find(r => r.highlight) || allResorts[0];
  const alternatives = allResorts.filter(r => r !== heroResort);

  const heroImage = getResortImage(heroResort.name, 0);

  resortsContent.innerHTML = `
    <div class="resort-recommendation">
      <!-- Destination Context -->
      <div class="resort-destination">
        <span class="destination-label">Resorts in</span>
        <span class="destination-name">${destination}</span>
      </div>

      <!-- Hero: Maya's Pick -->
      <article class="resort-hero">
        <div class="hero-image" style="background-image: url('${heroImage}')">
          <span class="hero-badge">Maya's Pick</span>
          <h3 class="hero-name">${heroResort.name}</h3>
          <p class="hero-tagline">"${heroResort.tagline}"</p>
        </div>
        <div class="hero-content">
          <button class="hero-cta" data-resort="${heroResort.name}">
            Tell me more
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </article>

      ${alternatives.length > 0 ? `
      <!-- Alternatives -->
      <div class="resort-alternatives">
        <span class="alternatives-label">Also worth considering</span>
        <div class="alternatives-grid">
          ${alternatives.map((resort, index) => {
            const image = getResortImage(resort.name, index + 1);
            return `
              <button class="alternative-card" data-resort="${resort.name}">
                <div class="alternative-image" style="background-image: url('${image}')"></div>
                <div class="alternative-info">
                  <span class="alternative-name">${resort.name}</span>
                  <span class="alternative-tagline">${resort.tagline}</span>
                </div>
              </button>
            `;
          }).join('')}
        </div>
      </div>
      ` : ''}
    </div>
  `;

  // Attach click handlers
  resortsContent.querySelectorAll('[data-resort]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const resortName = (e.currentTarget as HTMLElement).dataset.resort;
      if (resortName && onResortSelect) {
        onResortSelect(resortName);
      }
    });
  });
}

// ============================================
// Resort Detail View - The Deep Dive
// Shows when user clicks "Tell me more"
// ============================================

export function renderResortDetail(resortName: string, destination: string, tagline?: string) {
  const image = getResortImage(resortName, 0);
  const highlights = getResortHighlights(resortName);
  const displayTagline = tagline || 'A stunning Caribbean retreat';

  resortsContent.innerHTML = `
    <div class="resort-detail">
      <!-- Back Button -->
      <button class="detail-back" id="back-to-resorts">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back to options
      </button>

      <!-- Hero Image -->
      <div class="detail-image" style="background-image: url('${image}')">
        <div class="detail-location">${destination}</div>
      </div>

      <!-- Resort Info -->
      <div class="detail-content">
        <h2 class="detail-name">${resortName}</h2>
        <p class="detail-tagline">"${displayTagline}"</p>

        <!-- Highlights -->
        <div class="detail-section">
          <h3 class="detail-section-title">Why Maya loves it</h3>
          <ul class="detail-highlights">
            ${highlights.map(h => `
              <li class="detail-highlight">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                ${h}
              </li>
            `).join('')}
          </ul>
        </div>
      </div>
    </div>
  `;

  // Back button handler
  document.getElementById('back-to-resorts')?.addEventListener('click', () => {
    if (currentResortsData) {
      renderDynamicResorts(currentResortsData);
    }
  });
}

// ============================================
// Cabin Detail View - The Upsell Moment
// Shows what each cabin class really offers
// ============================================

export function renderCabinDetail(cabinClass: string, price: number) {
  const experience = getCabinExperience(cabinClass);
  const isUpperClass = cabinClass === 'Upper Class';

  // SVG icons for each highlight type
  const icons: Record<string, string> = {
    meal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 8h1a4 4 0 010 8h-1M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8zM6 1v3M10 1v3M14 1v3"/></svg>',
    screen: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>',
    power: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
    legroom: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 11V7a2 2 0 014 0v6l6.5 3.5M12 17h5l2 4h2"/><circle cx="5" cy="4" r="2"/></svg>',
    checkin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>',
    seat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 18v3h3M17 18v3h3M4 12V6a2 2 0 012-2h12a2 2 0 012 2v6M2 12h20v6H2z"/></svg>',
    dining: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2M7 2v20M21 15V2v0a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>',
    bag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6h12l1 14H5L6 6zM9 6V4a3 3 0 016 0v2"/></svg>',
    amenity: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V8zM7 6V4M17 6V4"/><path d="M3 10h18"/></svg>',
    chauffeur: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v5h-7V8zM5 21a2 2 0 100-4 2 2 0 000 4zM19 21a2 2 0 100-4 2 2 0 000 4z"/></svg>',
    lounge: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 10V7a2 2 0 00-2-2H6a2 2 0 00-2 2v3M2 14v3a2 2 0 002 2h16a2 2 0 002-2v-3M2 10h20v4H2z"/></svg>',
    bed: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 4v16M2 8h18a2 2 0 012 2v10M2 17h20M6 8v9"/></svg>',
    bar: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M8 22h8M12 11v11M5 2l7 9 7-9"/></svg>',
    arrivals: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 12h8m0 0l-4-4m4 4l-4 4"/><path d="M20 4v16"/></svg>'
  };

  flightsContent.innerHTML = `
    <div class="cabin-detail ${isUpperClass ? 'upper-class-detail' : ''}">
      <!-- Back Button -->
      <button class="detail-back" id="back-to-cabins">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        All cabin classes
      </button>

      <!-- Hero Image -->
      <div class="cabin-hero" style="background-image: url('${experience.image}')">
        ${isUpperClass ? '<span class="cabin-hero-badge">The Ultimate Experience</span>' : ''}
      </div>

      <!-- Cabin Info -->
      <div class="cabin-detail-content">
        <span class="cabin-detail-class">${experience.name}</span>
        <h2 class="cabin-detail-tagline">"${experience.tagline}"</h2>

        <div class="cabin-detail-price">
          <span class="price-from">From</span>
          <span class="price-amount">£${price.toLocaleString()}</span>
          <span class="price-pp">per person</span>
        </div>

        <!-- The Experience -->
        <div class="cabin-experience">
          <h3 class="experience-title">${isUpperClass ? 'The Upper Class Experience' : 'What\'s Included'}</h3>
          <ul class="experience-highlights">
            ${experience.highlights.map((h, i) => `
              <li class="experience-highlight" style="animation-delay: ${(i + 1) * 80}ms">
                <span class="highlight-icon">${icons[h.icon] || icons.checkin}</span>
                <span class="highlight-text">${h.text}</span>
              </li>
            `).join('')}
          </ul>
        </div>

        ${experience.priceHint ? `
          <div class="cabin-hint">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="16" height="16">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            ${experience.priceHint}
          </div>
        ` : ''}
      </div>
    </div>
  `;

  // Back button handler
  document.getElementById('back-to-cabins')?.addEventListener('click', () => {
    if (currentFlightsData) {
      renderDynamicFlights(currentFlightsData);
    }
  });
}

// Get the tagline for a resort from stored data
export function getStoredResortTagline(resortName: string): string | undefined {
  if (!currentResortsData) return undefined;
  const resort = currentResortsData.resorts.find(
    r => r.name.toLowerCase() === resortName.toLowerCase()
  );
  return resort?.tagline;
}

// Get the destination from stored data
export function getStoredDestination(): string {
  return currentResortsData?.destination || 'Caribbean';
}

// ============================================
// Dynamic Flight Rendering - The Flight Ticket
// ONE flight with cabin class options
// ============================================

export function renderDynamicFlights(data: ShowFlightsData) {
  const {
    route,
    origin,
    origin_code,
    destination,
    destination_code,
    outbound_date,
    return_date,
    flights
  } = data;

  if (!flights || flights.length === 0) {
    renderFlights(MOCK_FLIGHTS, {});
    return;
  }

  // Store for back navigation from cabin detail
  currentFlightsData = data;

  // Parse route
  let fromCity = origin || 'London';
  let toCity = destination || 'Barbados';
  let fromCode = origin_code || getAirportCode(fromCity);
  let toCode = destination_code || getAirportCode(toCity);

  if (route && !origin) {
    const routeParts = route.toLowerCase().split(/\s+to\s+/);
    if (routeParts.length === 2) {
      fromCity = routeParts[0].trim();
      toCity = routeParts[1].trim();
      fromCode = getAirportCode(fromCity);
      toCode = getAirportCode(toCity);
    }
  }

  const duration = estimateFlightDuration(fromCity, toCity);
  const flightNumber = generateFlightNumber(0);

  // Use first flight's times as the "primary" flight
  const primaryFlight = flights[0];
  const departTime = primaryFlight.departure_time || '09:15';
  const arriveTime = primaryFlight.arrival_time || '13:45';

  // Build cabin options from all flights passed
  const cabinOptions = flights.map((flight: MayaFlightOption) => {
    const cabin = normalizeCabinClass(flight.cabin_class);
    const price = parsePrice(flight.price_from);
    return { cabin, price };
  });

  // Ensure we have all 3 classes (fill with defaults if needed)
  const cabinMap: Record<string, number> = {};
  cabinOptions.forEach(opt => { cabinMap[opt.cabin] = opt.price; });

  const allCabins = [
    { cabin: 'Economy', price: cabinMap['Economy'] || 649, cssClass: 'economy' },
    { cabin: 'Premium', price: cabinMap['Premium'] || 1249, cssClass: 'premium' },
    { cabin: 'Upper Class', price: cabinMap['Upper Class'] || 2899, cssClass: 'upper-class', highlight: true }
  ];

  flightsContent.innerHTML = `
    <article class="flight-ticket">
      <!-- Date Header -->
      <div class="ticket-header">
        <div class="ticket-date">
          <span class="ticket-label">Outbound</span>
          <span class="ticket-value">${outbound_date}</span>
        </div>
        ${return_date ? `
        <div class="ticket-date">
          <span class="ticket-label">Return</span>
          <span class="ticket-value">${return_date}</span>
        </div>
        ` : ''}
      </div>

      <!-- The Route -->
      <div class="ticket-route">
        <div class="ticket-endpoint">
          <span class="ticket-code">${fromCode}</span>
          <span class="ticket-city">${capitalize(fromCity)}</span>
        </div>
        <div class="ticket-path">
          <div class="path-line"></div>
          <div class="path-plane">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style="transform: rotate(90deg)">
              <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
            </svg>
          </div>
          <div class="path-line"></div>
        </div>
        <div class="ticket-endpoint">
          <span class="ticket-code">${toCode}</span>
          <span class="ticket-city">${capitalize(toCity)}</span>
        </div>
      </div>

      <!-- Flight Details -->
      <div class="ticket-details">
        <div class="detail-block">
          <span class="detail-label">Departs</span>
          <span class="detail-value">${departTime}</span>
        </div>
        <div class="detail-block detail-center">
          <span class="detail-label">Duration</span>
          <span class="detail-value">${duration}</span>
          <span class="detail-sub">Direct</span>
        </div>
        <div class="detail-block detail-right">
          <span class="detail-label">Arrives</span>
          <span class="detail-value">${arriveTime}</span>
          <span class="detail-sub">local time</span>
        </div>
      </div>

      <!-- Flight Number -->
      <div class="ticket-flight-info">
        <span class="flight-number">${flightNumber}</span>
        <span class="flight-aircraft">Virgin Atlantic</span>
      </div>

      <!-- Cabin Class Options -->
      <div class="ticket-divider">
        <span>Choose your cabin</span>
      </div>

      <div class="cabin-options">
        ${allCabins.map(opt => `
          <button class="cabin-option ${opt.cssClass}${opt.highlight ? ' cabin-highlight' : ''}" data-cabin="${opt.cabin}" data-price="${opt.price}">
            <span class="cabin-name">${opt.cabin}</span>
            <span class="cabin-price">£${opt.price.toLocaleString()}</span>
            <span class="cabin-pp">per person</span>
            ${opt.highlight ? '<span class="cabin-badge">Recommended</span>' : ''}
          </button>
        `).join('')}
      </div>
    </article>
  `;

  // Attach click handlers for cabin deep dive
  flightsContent.querySelectorAll('[data-cabin]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const cabinClass = (e.currentTarget as HTMLElement).dataset.cabin;
      const price = parseInt((e.currentTarget as HTMLElement).dataset.price || '0', 10);
      if (cabinClass) {
        renderCabinDetail(cabinClass, price);
        if (onCabinSelect) {
          onCabinSelect(cabinClass);
        }
      }
    });
  });
}

// ============================================
// Legacy Renderers (fallback)
// ============================================

export function renderFlights(flights: Flight[], _args?: Record<string, unknown>) {
  const curated = flights.slice(0, 3);

  flightsContent.innerHTML = curated.map(flight => `
    <article class="flight-card">
      <div class="flight-route">
        <div class="flight-endpoint">
          <div class="flight-code">${getAirportCode(flight.departure)}</div>
          <div class="flight-city">${flight.departure}</div>
        </div>
        <div class="flight-path">
          <div class="flight-line"></div>
          <span class="flight-icon">✈</span>
          <div class="flight-line"></div>
        </div>
        <div class="flight-endpoint">
          <div class="flight-code">${getAirportCode(flight.arrival)}</div>
          <div class="flight-city">${flight.arrival}</div>
        </div>
      </div>

      <div class="flight-times">
        <span class="flight-time">${flight.departureTime}</span>
        <div class="flight-duration">
          <span>${flight.duration}</span>
          ${flight.stops === 0 ? 'Direct' : `${flight.stops} stop`}
        </div>
        <span class="flight-time">${flight.arrivalTime}</span>
      </div>

      <div class="flight-footer">
        <span class="flight-class ${flight.class.toLowerCase().replace(' ', '-')}">${flight.class}</span>
        <div class="flight-price">
          <span class="price-amount">£${flight.price.toLocaleString()}</span>
          <span class="price-label">per person</span>
        </div>
      </div>
    </article>
  `).join('');
}

export function renderResorts(resorts: Resort[], _args?: Record<string, unknown>) {
  const curated = resorts.slice(0, 3);

  resortsContent.innerHTML = curated.map(resort => `
    <article class="resort-card">
      <div class="resort-image" style="background-image: url('${resort.image}')">
        <span class="resort-rating">${'★'.repeat(Math.floor(resort.rating))}</span>
      </div>
      <div class="resort-info">
        <h3 class="resort-name">${resort.name}</h3>
        <p class="resort-location">${resort.location}</p>
        <div class="resort-highlights">
          ${resort.highlights.slice(0, 3).map(h => `<span class="highlight-tag">${h}</span>`).join('')}
        </div>
        <div class="resort-footer">
          <div class="resort-price">
            <span class="price-amount">£${resort.pricePerNight}</span>
            <span class="price-label">/ night</span>
          </div>
          <button class="select-btn" data-resort="${resort.name}">Tell me more</button>
        </div>
      </div>
    </article>
  `).join('');

  resortsContent.querySelectorAll('.select-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const resortName = (e.currentTarget as HTMLElement).dataset.resort;
      if (resortName && onResortSelect) {
        onResortSelect(resortName);
      }
    });
  });
}

// ============================================
// Helpers
// ============================================

function capitalize(str: string): string {
  return str.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
}

// ============================================
// Checkout / Booking Summary
// The Closing Experience
// ============================================

const checkoutOverlay = document.getElementById('checkout-overlay')!;
const checkoutContent = document.getElementById('checkout-content')!;
const checkoutBack = document.getElementById('checkout-back')!;
const appMain = document.getElementById('app')!;

// Booking data interface
export interface BookingData {
  destination: string;
  resort_name: string;
  resort_nights: number;
  room_type?: string;
  origin_code: string;
  origin_city: string;
  destination_code: string;
  departure_time: string;
  arrival_time: string;
  outbound_date: string;
  return_date?: string;
  cabin_class: string;
  guests: number;
  total_price: number;
  price_per_person: number;
  inclusions?: string[];
}

// Callbacks
let onPaymentComplete: ((booking: BookingData) => void) | null = null;
let onCheckoutClose: (() => void) | null = null;
let currentBookingData: BookingData | null = null;

export function setOnPaymentComplete(callback: (booking: BookingData) => void) {
  onPaymentComplete = callback;
}

export function setOnCheckoutClose(callback: () => void) {
  onCheckoutClose = callback;
}

// Show checkout overlay with booking summary
export function showCheckout(data: BookingData) {
  currentBookingData = data;

  // Add checkout mode to app (shrinks Maya to PiP)
  appMain.classList.add('checkout-mode');

  // Render booking summary
  renderBookingSummary(data);

  // Show overlay
  checkoutOverlay.classList.add('visible');
  checkoutOverlay.setAttribute('aria-hidden', 'false');
}

// Hide checkout overlay
export function hideCheckout() {
  checkoutOverlay.classList.remove('visible');
  checkoutOverlay.setAttribute('aria-hidden', 'true');
  appMain.classList.remove('checkout-mode');

  if (onCheckoutClose) {
    onCheckoutClose();
  }
}

// Back button handler
checkoutBack.addEventListener('click', hideCheckout);

// Render booking summary
function renderBookingSummary(data: BookingData) {
  const resortImage = getResortImage(data.resort_name, 0);
  const inclusions = data.inclusions || [
    'Return flights',
    `${data.resort_nights} nights accommodation`,
    'Airport transfers',
    'ATOL protection',
    '24/7 support',
    'Clubhouse lounge access'
  ];

  checkoutContent.innerHTML = `
    <div class="booking-summary">
      <!-- Left Column: Resort Image (spans full height) -->
      <div class="booking-resort">
        <div class="booking-resort-image" style="background-image: url('${resortImage}')"></div>
        <div class="booking-resort-info">
          <h2 class="booking-resort-name">${data.resort_name}</h2>
          <p class="booking-resort-detail">${data.resort_nights} nights${data.room_type ? ` · ${data.room_type}` : ''}</p>
        </div>
      </div>

      <!-- Right Column: Booking Details -->
      <div class="booking-main">
        <!-- Header -->
        <header class="booking-header">
          <span class="booking-eyebrow">Your journey awaits</span>
          <h1 class="booking-title">Your ${capitalize(data.destination)} escape</h1>
          <p class="booking-dates">${data.outbound_date}${data.return_date ? ` – ${data.return_date}` : ''}</p>
        </header>

        <!-- Flight Route -->
        <div class="booking-flight">
          <div class="booking-flight-label">Your flight</div>
          <div class="booking-route">
            <div class="booking-airport">
              <div class="booking-airport-code">${data.origin_code}</div>
              <div class="booking-airport-city">${data.origin_city}</div>
              <div class="booking-airport-time">${data.departure_time}</div>
            </div>
            <div class="booking-flight-path">
              <div class="booking-flight-line"></div>
              <svg class="booking-flight-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
              </svg>
              <div class="booking-flight-line"></div>
            </div>
            <div class="booking-airport">
              <div class="booking-airport-code">${data.destination_code}</div>
              <div class="booking-airport-city">${capitalize(data.destination)}</div>
              <div class="booking-airport-time">${data.arrival_time}</div>
            </div>
          </div>
          <div class="booking-flight-meta">
            <span class="booking-cabin-class">${data.cabin_class}</span>
            <span>Direct</span>
            <span>8h 30m</span>
          </div>
        </div>

        <!-- Inclusions -->
        <div class="booking-inclusions">
          <div class="booking-inclusions-title">What's included</div>
          <div class="booking-inclusions-grid">
            ${inclusions.map(inc => `
              <div class="booking-inclusion">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>${inc}</span>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Total -->
        <div class="booking-total">
          <div>
            <div class="booking-total-label">Total</div>
            <span class="booking-total-guests">${data.guests} guest${data.guests > 1 ? 's' : ''}</span>
          </div>
          <div class="booking-total-price">
            <span class="booking-total-amount">£${data.total_price.toLocaleString()}</span>
            <span class="booking-total-pp">£${data.price_per_person.toLocaleString()} pp</span>
          </div>
        </div>

        <!-- Payment -->
        <div class="booking-payment">
          <button class="payment-btn payment-btn-apple" id="pay-apple">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Pay
          </button>
          <div class="payment-divider">or</div>
          <button class="payment-btn payment-btn-card" id="pay-card">
            Pay by card
          </button>
        </div>

        <!-- Trust Badges -->
        <div class="booking-trust">
          <div class="trust-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            ATOL protected
          </div>
          <div class="trust-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
            </svg>
            24/7 support
          </div>
        </div>
      </div>
    </div>
  `;

  // Attach payment handlers
  document.getElementById('pay-apple')?.addEventListener('click', () => handlePayment('apple'));
  document.getElementById('pay-card')?.addEventListener('click', () => handlePayment('card'));
}

// Handle payment (simulated for demo)
function handlePayment(method: 'apple' | 'card') {
  if (!currentBookingData) return;

  // Show processing state
  checkoutContent.innerHTML = `
    <div class="booking-processing">
      <div class="processing-spinner"></div>
      <p class="processing-text">Processing your booking...</p>
    </div>
  `;

  // Simulate payment processing
  setTimeout(() => {
    renderBookingConfirmation(currentBookingData!);
    if (onPaymentComplete) {
      onPaymentComplete(currentBookingData!);
    }
  }, 2000);
}

// Render confirmation / celebration - Two column layout with hero image
function renderBookingConfirmation(data: BookingData) {
  const resortImage = getResortImage(data.resort_name, 0);
  const bookingRef = generateBookingRef();

  checkoutContent.innerHTML = `
    <div class="booking-confirmation">
      <!-- Left: Hero Image with Celebration -->
      <div class="confirmation-hero" style="background-image: url('${resortImage}')">
        <div class="confirmation-celebration">
          <span class="confirmation-eyebrow">You're going to</span>
          <h1 class="confirmation-destination">${capitalize(data.destination)}</h1>
          <p class="confirmation-dates">${data.outbound_date}${data.return_date ? ` – ${data.return_date}` : ''}</p>
        </div>
      </div>

      <!-- Right: Confirmation Details -->
      <div class="confirmation-details">
        <!-- Header with checkmark -->
        <header class="confirmation-header">
          <div class="confirmation-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <div class="confirmation-status">
            <h2 class="confirmation-title">Booking confirmed</h2>
            <p class="confirmation-ref">Reference: <strong>${bookingRef}</strong></p>
          </div>
        </header>

        <!-- Boarding Pass Style Summary -->
        <div class="confirmation-pass">
          <div class="pass-top">
            <div class="pass-route">
              <span class="pass-code">${data.origin_code}</span>
              <svg class="pass-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
              <span class="pass-code">${data.destination_code}</span>
            </div>
          </div>
          <div class="pass-bottom">
            <div>
              <span class="pass-detail-label">Resort</span>
              <span class="pass-detail-value">${data.resort_name}</span>
            </div>
            <div>
              <span class="pass-detail-label">Nights</span>
              <span class="pass-detail-value">${data.resort_nights}</span>
            </div>
            <div>
              <span class="pass-detail-label">Class</span>
              <span class="pass-detail-value">${data.cabin_class}</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="confirmation-footer">
          <p class="confirmation-message">
            We've sent your confirmation to your email.<br/>
            Time to start getting excited!
          </p>
          <button class="confirmation-btn" id="confirmation-done">
            Done
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;

  // Done button closes checkout
  document.getElementById('confirmation-done')?.addEventListener('click', hideCheckout);

  // Update back button text
  checkoutBack.querySelector('span')!.textContent = 'Close';
}

// Generate a realistic booking reference
function generateBookingRef(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const nums = '0123456789';
  let ref = 'VA';
  for (let i = 0; i < 2; i++) ref += chars[Math.floor(Math.random() * chars.length)];
  for (let i = 0; i < 4; i++) ref += nums[Math.floor(Math.random() * nums.length)];
  return ref;
}
